import { useEffect, useRef, useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import UserOne from "../../images/user/user-00.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faGlobe,
    faHouse,
    faPlus,
    faFilm,
    faGraduationCap,
    faVideo,
    faBroadcastTower,
    faHeart,
    faCommentDots,
} from "@fortawesome/free-solid-svg-icons";
import { faBuilding } from "@fortawesome/free-solid-svg-icons";
import { faNewspaper } from "@fortawesome/free-solid-svg-icons";

import ResponsiveNavLink from "@/Components/ResponsiveNavLink";

const DropdownUser = () => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [rolesDropdownOpen, setRolesDropdownOpen] = useState(false);
    const user = usePage().props.auth?.user;

    const { props: sharedProps } = usePage();
    const { buckets, tenantSlug, globalSettings } = sharedProps;
    const organizationsLabel =
        globalSettings?.organizations_label?.trim() || "Organizations";
    const videosLabel = globalSettings?.videos_label?.trim() || "Videos";

    // Check if user has go_live permission
    const hasGoLivePermission = user?.permissions?.some(
        (permission) => permission.permission_name === "go_live",
    );

    // Check if user has view_live permission
    const hasViewLivePermission = user?.permissions?.some(
        (permission) => permission.permission_name === "view_live",
    );

    const profileImagesBucket = buckets?.profile_images?.name ?? "";
    const profileImagesRegion = buckets?.profile_images?.region ?? "";
    const profileImageBaseUrl = `https://${profileImagesBucket}.s3.${profileImagesRegion}.amazonaws.com/${tenantSlug}/`;

    const trigger = useRef(null);
    const dropdown = useRef(null);
    const rolesTrigger = useRef(null);
    const rolesDropdown = useRef(null);

    // Close user dropdown on click outside
    useEffect(() => {
        const clickHandler = (event) => {
            if (!dropdown.current || !trigger.current) return;
            if (
                !dropdownOpen ||
                dropdown.current.contains(event.target) ||
                trigger.current.contains(event.target)
            ) {
                return;
            }
            setDropdownOpen(false);
        };
        document.addEventListener("click", clickHandler);
        return () => document.removeEventListener("click", clickHandler);
    }, [dropdownOpen]);

    // Close roles dropdown on click outside
    useEffect(() => {
        const clickHandler = (event) => {
            if (!rolesDropdown.current || !rolesTrigger.current) return;
            if (
                !rolesDropdownOpen ||
                rolesDropdown.current.contains(event.target) ||
                rolesTrigger.current.contains(event.target)
            ) {
                return;
            }
            setRolesDropdownOpen(false);
        };
        document.addEventListener("click", clickHandler);
        return () => document.removeEventListener("click", clickHandler);
    }, [rolesDropdownOpen]);

    // Close if the esc key is pressed
    useEffect(() => {
        const keyHandler = (event) => {
            if (dropdownOpen && event.keyCode === 27) setDropdownOpen(false);
            if (rolesDropdownOpen && event.keyCode === 27)
                setRolesDropdownOpen(false);
        };
        document.addEventListener("keydown", keyHandler);
        return () => document.removeEventListener("keydown", keyHandler);
    }, [dropdownOpen, rolesDropdownOpen]);

    return (
        <div className="relative flex items-center gap-2">
            {user && (
                <span className="hidden text-right lg:block">
                    <span className="block text-sm font-medium text-black dark:text-white">
                        {user.first_name && user.last_name
                            ? `${user.first_name} ${user.last_name}`
                            : user.email}
                    </span>
                    {/* Roles label or dropdown under the name */}
                    <span className="block text-xs relative">
                        {Array.isArray(sharedProps?.auth?.user?.roles) &&
                        sharedProps?.auth?.user?.roles?.length === 1 ? (
                            <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                                {sharedProps.auth.user.roles[0]?.role_name ??
                                    "No Roles"}
                            </span>
                        ) : Array.isArray(sharedProps?.auth?.user?.roles) &&
                          sharedProps?.auth?.user?.roles?.length > 1 ? (
                            <>
                                <button
                                    ref={rolesTrigger}
                                    type="button"
                                    className="flex items-center gap-1 px-1 py-0.5hover:bg-gray-100 dark:hover:bg-gray-700"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setRolesDropdownOpen((open) => !open);
                                    }}
                                    tabIndex={0}
                                >
                                    Multiple Roles
                                    <svg
                                        className="w-3 h-3 ml-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </svg>
                                </button>
                                {rolesDropdownOpen && (
                                    <ul
                                        ref={rolesDropdown}
                                        style={{ top: "40px", left: 0 }}
                                        className="absolute left-0 w-44 bg-white dark:bg-boxdark dark:border-gray-700 rounded shadow-lg z-50 text-left"
                                    >
                                        {sharedProps.auth.user.roles.map(
                                            (role, idx) => (
                                                <li
                                                    key={
                                                        (role?.role_name ??
                                                            "role") + idx
                                                    }
                                                    className="px-4 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-default text-left"
                                                >
                                                    {role?.role_name ??
                                                        "No Role Name"}
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                )}
                            </>
                        ) : (
                            <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-400">
                                No Roles
                            </span>
                        )}
                    </span>
                </span>
            )}
            {/* User Dropdown Trigger */}
            <Link
                ref={trigger}
                onClick={(event) => {
                    event.preventDefault();
                    setDropdownOpen(!dropdownOpen);
                }}
                className="flex items-center gap-4"
                to="#"
            >
                <span className="h-12 w-12 rounded-full">
                    <img
                        className="rounded-full"
                        src={
                            user?.avatar_image_file_name
                                ? profileImageBaseUrl +
                                  user.avatar_image_file_name
                                : user?.login_image_url || UserOne
                        }
                        alt="User"
                    />
                </span>
                <svg
                    className="fill-current sm:block"
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.410765 0.910734C0.736202 0.585297 1.26384 0.585297 1.58928 0.910734L6.00002 5.32148L10.4108 0.910734C10.7362 0.585297 11.2638 0.585297 11.5893 0.910734C11.9147 1.23617 11.9147 1.76381 11.5893 2.08924L6.58928 7.08924C6.26384 7.41468 5.7362 7.41468 5.41077 7.08924L0.410765 2.08924C0.0853277 1.76381 0.0853277 1.23617 0.410765 0.910734Z"
                        fill=""
                    />
                </svg>
            </Link>
            {/* Dropdown Start */}
            <div
                ref={dropdown}
                onFocus={() => setDropdownOpen(true)}
                onBlur={() => setDropdownOpen(false)}
                style={{ top: "60px" }}
                className={`absolute right-0 mt-4 flex w-62.5 flex-col rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark ${
                    dropdownOpen ? "block" : "hidden"
                }`}
            >
                {user && (
                    <ul className="flex xl:hidden flex-col gap-4 border-b border-stroke px-6 py-7.5 dark:border-strokedark">
                        <li className="block xl:hidden">
                            <Link
                                href={route("users.posts.index")}
                                className="flex items-center gap-2 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                            >
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={faHouse}
                                    className="cursor-pointer"
                                />
                                Home
                            </Link>
                        </li>
                        {globalSettings?.donation_url && (
                            <li className="block xl:hidden">
                                <a
                                    href={globalSettings.donation_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                                    style={{ color: "#0077B6" }}
                                >
                                    <FontAwesomeIcon
                                        icon={faHeart}
                                        className="cursor-pointer"
                                    />
                                    Give
                                </a>
                            </li>
                        )}
                        <li className="block xl:hidden">
                            <Link
                                href={route("user.organizations.index")}
                                className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                            >
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={faBuilding}
                                    className="cursor-pointer"
                                />
                                {organizationsLabel}
                            </Link>
                        </li>

                        <li className="block xl:hidden">
                            <Link
                                href={route("users.blogs.index")}
                                className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                            >
                                <FontAwesomeIcon
                                    icon={faNewspaper}
                                    className="cursor-pointer"
                                />
                                Blog
                            </Link>
                        </li>
                        {globalSettings?.courses_service_enabled && (
                            <li className="block xl:hidden">
                                <Link
                                    href={route("courses.index")}
                                    className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                                >
                                    <FontAwesomeIcon
                                        icon={faGraduationCap}
                                        className="cursor-pointer"
                                    />
                                    Courses
                                </Link>
                            </li>
                        )}
                        {globalSettings?.direct_messaging_enabled && (
                            <li className="block xl:hidden">
                                <Link
                                    href={route("messages.index")}
                                    className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                                >
                                    <FontAwesomeIcon
                                        icon={faCommentDots}
                                        className="cursor-pointer"
                                    />
                                    Messages
                                </Link>
                            </li>
                        )}
                        {globalSettings?.vod_service_enabled && (
                            <li className="block xl:hidden">
                                <Link
                                    href={route("videos.index")}
                                    className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                                >
                                    <FontAwesomeIcon
                                        icon={faFilm}
                                        className="cursor-pointer"
                                    />
                                    {videosLabel}
                                </Link>
                            </li>
                        )}

                        {/* Live Streaming Links - only show if user has go_live permission */}
                        {user &&
                            hasGoLivePermission &&
                            globalSettings?.live_streaming_enabled && (
                                <li className="block xl:hidden">
                                    <Link
                                        href={route("live.start")}
                                        className="flex gap-3.5 text-sm font-medium duration-300 ease-in-out text-red hover:text-gray-600 dark:hover:text-gray-400 lg:text-base text-center"
                                    >
                                        <FontAwesomeIcon
                                            icon={faBroadcastTower}
                                            className="cursor-pointer"
                                        />
                                        Go Live
                                    </Link>
                                </li>
                            )}

                        {/* View Live Link - only show if user has view_live permission */}
                        {user &&
                            hasViewLivePermission &&
                            globalSettings?.live_streaming_enabled && (
                                <li className="block xl:hidden">
                                    <Link
                                        href={route("live.view")}
                                        className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                                    >
                                        <FontAwesomeIcon
                                            icon={faVideo}
                                            className="cursor-pointer"
                                        />
                                        LIVE
                                    </Link>
                                </li>
                            )}

                        {globalSettings?.about_us_page_url && (
                            <li className="block xl:hidden">
                                <a
                                    href={globalSettings.about_us_page_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                                >
                                    <FontAwesomeIcon
                                        icon={faGlobe}
                                        className="cursor-pointer"
                                    />
                                    About Us
                                </a>
                            </li>
                        )}
                    </ul>
                )}
                {!user && (
                    <ul className="flex flex-col gap-4 border-b border-stroke px-6 py-7.5 dark:border-strokedark">
                        <li className="block xl:hidden">
                            <Link
                                href={route("users.posts.index")}
                                className="flex items-center gap-2 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                            >
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={faHouse}
                                    className="cursor-pointer"
                                />
                                Home
                            </Link>
                        </li>
                        {globalSettings?.donation_url && (
                            <li className="block xl:hidden">
                                <a
                                    href={globalSettings.donation_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                                    style={{ color: "#0077B6" }}
                                >
                                    <FontAwesomeIcon
                                        icon={faHeart}
                                        className="cursor-pointer"
                                    />
                                    Give
                                </a>
                            </li>
                        )}
                        <li className="block xl:hidden">
                            <Link
                                href={route("user.organizations.index")}
                                className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                            >
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={faBuilding}
                                    className="cursor-pointer"
                                />
                                {organizationsLabel}
                            </Link>
                        </li>
                        <li className="block xl:hidden">
                            <Link
                                href={route("users.blogs.index")}
                                className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                            >
                                <FontAwesomeIcon
                                    icon={faNewspaper}
                                    className="cursor-pointer"
                                />
                                Blog
                            </Link>
                        </li>
                        {globalSettings?.courses_service_enabled && (
                            <li className="block xl:hidden">
                                <Link
                                    href={route("courses.index")}
                                    className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                                >
                                    <FontAwesomeIcon
                                        icon={faGraduationCap}
                                        className="cursor-pointer"
                                    />
                                    Courses
                                </Link>
                            </li>
                        )}
                        {globalSettings?.direct_messaging_enabled && (
                            <li className="block xl:hidden">
                                <Link
                                    href={route("messages.index")}
                                    className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                                >
                                    <FontAwesomeIcon
                                        icon={faCommentDots}
                                        className="cursor-pointer"
                                    />
                                    Messages
                                </Link>
                            </li>
                        )}
                        {globalSettings?.vod_service_enabled && (
                            <li className="block xl:hidden">
                                <Link
                                    href={route("videos.index")}
                                    className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                                >
                                    <FontAwesomeIcon
                                        icon={faFilm}
                                        className="cursor-pointer"
                                    />
                                    {videosLabel}
                                </Link>
                            </li>
                        )}
                        {globalSettings?.about_us_page_url && (
                            <li className="block xl:hidden">
                                <a
                                    href={globalSettings.about_us_page_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                                >
                                    <FontAwesomeIcon
                                        size="lg"
                                        icon={faGlobe}
                                        className="cursor-pointer"
                                    />
                                    About Us
                                </a>
                            </li>
                        )}

                        {!user && (
                            <>
                                <li>
                                    <hr className=" lg:hidden border-t border-gray-300 dark:border-gray-600 opacity-40 pb-5 mt-5" />
                                    <ResponsiveNavLink
                                        method="get"
                                        href={route("login")}
                                        as="button"
                                        style={{
                                            marginBottom: "-12px",
                                            marginTop: "-5px",
                                        }}
                                        className="flex justify-start text-left items-center mt-3 gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                                    >
                                        {/* SVG Icon for Logout */}
                                        <svg
                                            className="fill-current"
                                            width="22"
                                            height="22"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10 17L15 12L10 7V10H3V14H10V17Z"
                                                fill="currentColor"
                                            />
                                            <path
                                                d="M20 3H12V5H20V19H12V21H20C21.1 21 22 20.1 22 19V5C22 3.9 21.1 3 20 3Z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                        Login
                                    </ResponsiveNavLink>

                                    <ResponsiveNavLink
                                        method="get"
                                        href={route("register")}
                                        as="button"
                                        style={{ marginBottom: "-12px" }}
                                        className="flex items-center mt-3 gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                                    >
                                        {/* SVG Icon for Logout */}
                                        <svg
                                            className="fill-current"
                                            width="22"
                                            height="22"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
                                                fill="currentColor"
                                            />
                                            <path
                                                d="M12 6.5c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 10c-2.33 0-4.31-1.46-5.11-3.5h10.22c-.8 2.04-2.78 3.5-5.11 3.5z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                        Register
                                        {/* <ResponsiveNavLink
                        method="post"
                        href={route("logout")}
                        as="button"
                    >
                        Log Out
                    </ResponsiveNavLink> */}
                                    </ResponsiveNavLink>
                                </li>
                            </>
                        )}
                    </ul>
                )}

                {user &&
                    user.permissions.some((permission) =>
                        permission.permission_name.includes(
                            "view_admin_dashboard",
                        ),
                    ) && (
                        <>
                            <ResponsiveNavLink
                                method="get"
                                href={route("admin")}
                                as="button"
                                style={{ marginBottom: "-12px" }}
                                className="flex items-center mt-3 gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                            >
                                {/* SVG Icon for Logout */}
                                <svg
                                    className="fill-current"
                                    width="22"
                                    height="22"
                                    viewBox="0 0 22 22"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M20.8656 8.86874C20.5219 8.49062 20.0406 8.28437 19.525 8.28437H19.4219C19.25 8.28437 19.1125 8.18124 19.0781 8.04374C19.0437 7.90624 18.975 7.80312 18.9406 7.66562C18.8719 7.52812 18.9406 7.39062 19.0437 7.28749L19.1125 7.21874C19.4906 6.87499 19.6969 6.39374 19.6969 5.87812C19.6969 5.36249 19.525 4.88124 19.1469 4.50312L17.8062 3.12812C17.0844 2.37187 15.8469 2.33749 15.0906 3.09374L14.9875 3.16249C14.8844 3.26562 14.7125 3.29999 14.5406 3.23124C14.4031 3.16249 14.2656 3.09374 14.0937 3.05937C13.9219 2.99062 13.8187 2.85312 13.8187 2.71562V2.54374C13.8187 1.47812 12.9594 0.618744 11.8937 0.618744H9.96875C9.45312 0.618744 8.97187 0.824994 8.62812 1.16874C8.25 1.54687 8.07812 2.02812 8.07812 2.50937V2.64687C8.07812 2.78437 7.975 2.92187 7.8375 2.99062C7.76875 3.02499 7.73437 3.02499 7.66562 3.05937C7.52812 3.12812 7.35625 3.09374 7.25312 2.99062L7.18437 2.88749C6.84062 2.50937 6.35937 2.30312 5.84375 2.30312C5.32812 2.30312 4.84687 2.47499 4.46875 2.85312L3.09375 4.19374C2.3375 4.91562 2.30312 6.15312 3.05937 6.90937L3.12812 7.01249C3.23125 7.11562 3.26562 7.28749 3.19687 7.39062C3.12812 7.52812 3.09375 7.63124 3.025 7.76874C2.95625 7.90624 2.85312 7.97499 2.68125 7.97499H2.57812C2.0625 7.97499 1.58125 8.14687 1.20312 8.52499C0.824996 8.86874 0.618746 9.34999 0.618746 9.86562L0.584371 11.7906C0.549996 12.8562 1.40937 13.7156 2.475 13.75H2.57812C2.75 13.75 2.8875 13.8531 2.92187 13.9906C2.99062 14.0937 3.05937 14.1969 3.09375 14.3344C3.12812 14.4719 3.09375 14.6094 2.99062 14.7125L2.92187 14.7812C2.54375 15.125 2.3375 15.6062 2.3375 16.1219C2.3375 16.6375 2.50937 17.1187 2.8875 17.4969L4.22812 18.8719C4.95 19.6281 6.1875 19.6625 6.94375 18.9062L7.04687 18.8375C7.15 18.7344 7.32187 18.7 7.49375 18.7687C7.63125 18.8375 7.76875 18.9062 7.94062 18.9406C8.1125 19.0094 8.21562 19.1469 8.21562 19.2844V19.4219C8.21562 20.4875 9.075 21.3469 10.1406 21.3469H12.0656C13.1312 21.3469 13.9906 20.4875 13.9906 19.4219V19.2844C13.9906 19.1469 14.0937 19.0094 14.2312 18.9406C14.3 18.9062 14.3344 18.9062 14.4031 18.8719C14.575 18.8031 14.7125 18.8375 14.8156 18.9406L14.8844 19.0437C15.2281 19.4219 15.7094 19.6281 16.225 19.6281C16.7406 19.6281 17.2219 19.4562 17.6 19.0781L18.975 17.7375C19.7312 17.0156 19.7656 15.7781 19.0094 15.0219L18.9406 14.9187C18.8375 14.8156 18.8031 14.6437 18.8719 14.5406C18.9406 14.4031 18.975 14.3 19.0437 14.1625C19.1125 14.025 19.25 13.9562 19.3875 13.9562H19.4906H19.525C20.5562 13.9562 21.4156 13.1312 21.45 12.0656L21.4844 10.1406C21.4156 9.72812 21.2094 9.21249 20.8656 8.86874ZM19.8344 12.1C19.8344 12.3062 19.6625 12.4781 19.4562 12.4781H19.3531H19.3187C18.5281 12.4781 17.8062 12.9594 17.5312 13.6469C17.4969 13.75 17.4281 13.8531 17.3937 13.9562C17.0844 14.6437 17.2219 15.5031 17.7719 16.0531L17.8406 16.1562C17.9781 16.2937 17.9781 16.5344 17.8406 16.6719L16.4656 18.0125C16.3625 18.1156 16.2594 18.1156 16.1906 18.1156C16.1219 18.1156 16.0187 18.1156 15.9156 18.0125L15.8469 17.9094C15.2969 17.325 14.4719 17.1531 13.7156 17.4969L13.5781 17.5656C12.8219 17.875 12.3406 18.5625 12.3406 19.3531V19.4906C12.3406 19.6969 12.1687 19.8687 11.9625 19.8687H10.0375C9.83125 19.8687 9.65937 19.6969 9.65937 19.4906V19.3531C9.65937 18.5625 9.17812 17.8406 8.42187 17.5656C8.31875 17.5312 8.18125 17.4625 8.07812 17.4281C7.80312 17.2906 7.52812 17.2562 7.25312 17.2562C6.77187 17.2562 6.29062 17.4281 5.9125 17.8062L5.84375 17.8406C5.70625 17.9781 5.46562 17.9781 5.32812 17.8406L3.9875 16.4656C3.88437 16.3625 3.88437 16.2594 3.88437 16.1906C3.88437 16.1219 3.88437 16.0187 3.9875 15.9156L4.05625 15.8469C4.64062 15.2969 4.8125 14.4375 4.50312 13.75C4.46875 13.6469 4.43437 13.5437 4.36562 13.4406C4.09062 12.7187 3.40312 12.2031 2.6125 12.2031H2.50937C2.30312 12.2031 2.13125 12.0312 2.13125 11.825L2.16562 9.89999C2.16562 9.76249 2.23437 9.69374 2.26875 9.62499C2.30312 9.59062 2.40625 9.52187 2.54375 9.52187H2.64687C3.4375 9.55624 4.15937 9.07499 4.46875 8.35312C4.50312 8.24999 4.57187 8.14687 4.60625 8.04374C4.91562 7.35624 4.77812 6.49687 4.22812 5.94687L4.15937 5.84374C4.02187 5.70624 4.02187 5.46562 4.15937 5.32812L5.53437 3.98749C5.6375 3.88437 5.74062 3.88437 5.80937 3.88437C5.87812 3.88437 5.98125 3.88437 6.08437 3.98749L6.15312 4.09062C6.70312 4.67499 7.52812 4.84687 8.28437 4.53749L8.42187 4.46874C9.17812 4.15937 9.65937 3.47187 9.65937 2.68124V2.54374C9.65937 2.40624 9.72812 2.33749 9.7625 2.26874C9.79687 2.19999 9.9 2.16562 10.0375 2.16562H11.9625C12.1687 2.16562 12.3406 2.33749 12.3406 2.54374V2.68124C12.3406 3.47187 12.8219 4.19374 13.5781 4.46874C13.6812 4.50312 13.8187 4.57187 13.9219 4.60624C14.6437 4.94999 15.5031 4.81249 16.0875 4.26249L16.1906 4.19374C16.3281 4.05624 16.5687 4.05624 16.7062 4.19374L18.0469 5.56874C18.15 5.67187 18.15 5.77499 18.15 5.84374C18.15 5.91249 18.1156 6.01562 18.0469 6.11874L17.9781 6.18749C17.3594 6.70312 17.1875 7.56249 17.4625 8.24999C17.4969 8.35312 17.5312 8.45624 17.6 8.55937C17.875 9.28124 18.5625 9.79687 19.3531 9.79687H19.4562C19.5937 9.79687 19.6625 9.86562 19.7312 9.89999C19.8 9.93437 19.8344 10.0375 19.8344 10.175V12.1Z"
                                        fill=""
                                    />
                                    <path
                                        d="M11 6.32498C8.42189 6.32498 6.32501 8.42186 6.32501 11C6.32501 13.5781 8.42189 15.675 11 15.675C13.5781 15.675 15.675 13.5781 15.675 11C15.675 8.42186 13.5781 6.32498 11 6.32498ZM11 14.1281C9.28126 14.1281 7.87189 12.7187 7.87189 11C7.87189 9.28123 9.28126 7.87186 11 7.87186C12.7188 7.87186 14.1281 9.28123 14.1281 11C14.1281 12.7187 12.7188 14.1281 11 14.1281Z"
                                        fill=""
                                    />
                                </svg>
                                Admin
                                {/* <ResponsiveNavLink
                        method="post"
                        href={route("logout")}
                        as="button"
                    >
                        Log Out
                    </ResponsiveNavLink> */}
                            </ResponsiveNavLink>
                        </>
                    )}
                {user && (
                    <ResponsiveNavLink
                        method="get"
                        href={`/users/${user.user_name}`}
                        as="button"
                        className="flex items-center mt-3 gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                    >
                        {/* SVG Icon for Logout */}
                        <svg
                            className="fill-current"
                            width="22"
                            height="22"
                            viewBox="0 0 22 22"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M11 9.62499C8.42188 9.62499 6.35938 7.59687 6.35938 5.12187C6.35938 2.64687 8.42188 0.618744 11 0.618744C13.5781 0.618744 15.6406 2.64687 15.6406 5.12187C15.6406 7.59687 13.5781 9.62499 11 9.62499ZM11 2.16562C9.28125 2.16562 7.90625 3.50624 7.90625 5.12187C7.90625 6.73749 9.28125 8.07812 11 8.07812C12.7188 8.07812 14.0938 6.73749 14.0938 5.12187C14.0938 3.50624 12.7188 2.16562 11 2.16562Z"
                                fill=""
                            />
                            <path
                                d="M17.7719 21.4156H4.2281C3.5406 21.4156 2.9906 20.8656 2.9906 20.1781V17.0844C2.9906 13.7156 5.7406 10.9656 9.10935 10.9656H12.925C16.2937 10.9656 19.0437 13.7156 19.0437 17.0844V20.1781C19.0094 20.8312 18.4594 21.4156 17.7719 21.4156ZM4.53748 19.8687H17.4969V17.0844C17.4969 14.575 15.4344 12.5125 12.925 12.5125H9.07498C6.5656 12.5125 4.5031 14.575 4.5031 17.0844V19.8687H4.53748Z"
                                fill=""
                            />
                        </svg>
                        My Profile
                        {/* <ResponsiveNavLink
                        method="post"
                        href={route("logout")}
                        as="button"
                    >
                        Log Out
                    </ResponsiveNavLink> */}
                    </ResponsiveNavLink>
                )}

                {user && (
                    <ResponsiveNavLink
                        method="get"
                        href={`/subscriptions`}
                        as="button"
                        className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                    >
                        {/* SVG Icon for Logout */}
                        <svg
                            className="w-5 h-5 stroke-current"
                            fill="none"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M12 2v20"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M17 5H9.5a3.5 3.5 0 0 0 0 7H14.5a3.5 3.5 0 0 1 0 7H6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        Subscriptions
                        {/* <ResponsiveNavLink
                        method="post"
                        href={route("logout")}
                        as="button"
                    >
                        Log Out
                    </ResponsiveNavLink> */}
                    </ResponsiveNavLink>
                )}
                {user && (
                    <ResponsiveNavLink
                        method="post"
                        href={route("logout")}
                        as="button"
                        className="flex items-center pb-5 gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                    >
                        {/* SVG Icon for Logout */}
                        <svg
                            className="fill-current"
                            width="22"
                            height="22"
                            viewBox="0 0 22 22"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M15.5375 0.618744H11.6531C10.7594 0.618744 10.0031 1.37499 10.0031 2.26874V4.64062C10.0031 5.05312 10.3469 5.39687 10.7594 5.39687C11.1719 5.39687 11.55 5.05312 11.55 4.64062V2.23437C11.55 2.16562 11.5844 2.13124 11.6531 2.13124H15.5375C16.3625 2.13124 17.0156 2.78437 17.0156 3.60937V18.3562C17.0156 19.1812 16.3625 19.8344 15.5375 19.8344H11.6531C11.5844 19.8344 11.55 19.8 11.55 19.7312V17.3594C11.55 16.9469 11.2062 16.6031 10.7594 16.6031C10.3125 16.6031 10.0031 16.9469 10.0031 17.3594V19.7312C10.0031 20.625 10.7594 21.3812 11.6531 21.3812H15.5375C17.2219 21.3812 18.5625 20.0062 18.5625 18.3562V3.64374C18.5625 1.95937 17.1875 0.618744 15.5375 0.618744Z"
                                fill=""
                            />
                            <path
                                d="M6.05001 11.7563H12.2031C12.6156 11.7563 12.9594 11.4125 12.9594 11C12.9594 10.5875 12.6156 10.2438 12.2031 10.2438H6.08439L8.21564 8.07813C8.52501 7.76875 8.52501 7.2875 8.21564 6.97812C7.90626 6.66875 7.42501 6.66875 7.11564 6.97812L3.67814 10.4844C3.36876 10.7938 3.36876 11.275 3.67814 11.5844L7.11564 15.0906C7.25314 15.2281 7.45939 15.3312 7.66564 15.3312C7.87189 15.3312 8.04376 15.2625 8.21564 15.125C8.52501 14.8156 8.52501 14.3344 8.21564 14.025L6.05001 11.7563Z"
                                fill=""
                            />
                        </svg>
                        Log Out
                        {/* <ResponsiveNavLink
                        method="post"
                        href={route("logout")}
                        as="button"
                    >
                        Log Out
                    </ResponsiveNavLink> */}
                    </ResponsiveNavLink>
                )}
            </div>
            {/* Dropdown End */}
        </div>
    );
};

export default DropdownUser;
